import React, { useCallback, useMemo, useState } from 'react';
import { DateRange } from 'react-day-picker';

import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@ais-ui/ui-core';
import { useOnMapPanelExit } from '../Map/MapPanel/useOnMapPanelExit';
import { EventList } from './EventList';
import { DetectorList } from './Detectors/DetectorList';
import { usePermissions } from '../Permissions/PermissionsProvider';
import { cn } from '@ais-ui/utils';
import { useLocalization } from '@ais-ui/locale-management';
import { useSettings } from '../Settings/SettingsProvider';

interface EventsProps {
  className: string;
}

const getDefaultDateRange = (lookBackMinutes: number) => ({
  from: new Date(Date.now() - lookBackMinutes * 60 * 1000),
  to: new Date(),
});

export const Events = React.memo(({ className }: EventsProps) => {
  const { messages } = useLocalization();
  const { settings } = useSettings();
  const onExit = useOnMapPanelExit();
  const [tab, setTab] = useState<'events' | 'eventdetector'>('events');
  const permissions = usePermissions();

  const defaultLookBackTime = useMemo(
    () => settings['ribbonPlugin.settings']?.event_lookback_minutes || 720,
    [settings]
  );

  const [date, setDate] = useState<DateRange | undefined>(() =>
    getDefaultDateRange(defaultLookBackTime)
  );

  const [isTrailing, setIsTrailing] = useState(true);

  const handleTabChange = useCallback((value: string) => {
    setTab(value as 'events' | 'eventdetector');
  }, []);

  return (
    <Card className={cn('flex flex-col w-[384px]', className)}>
      <CardHeader>
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <CardTitle className="text-lg font-medium text-muted-foreground">
              {messages.eventsListTitle}
            </CardTitle>
            <Button
              variant="ghost"
              size="icon"
              className="-mt-8 -mr-4"
              onClick={onExit}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
                className="h-4 w-4"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                ></path>
              </svg>
            </Button>
          </div>
          <Tabs
            value={tab}
            onValueChange={handleTabChange}
            className="w-full space-y-6 pt-4 h-full flex flex-col"
          >
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="events">
                {messages.eventsTabEvents}
              </TabsTrigger>

              <TabsTrigger
                id="events_detector_tab_button"
                value="eventdetector"
                disabled={!permissions.eventdetector}
              >
                {messages.eventsTabDetectors}
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </CardHeader>
      {tab === 'events' && (
        <EventList
          date={date}
          setDate={setDate}
          isTrailing={isTrailing}
          setIsTrailing={setIsTrailing}
          defaultLookBackTime={defaultLookBackTime}
        />
      )}
      {tab === 'eventdetector' && <DetectorList />}
    </Card>
  );
});

Events.displayName = 'Events';
